import { useState } from "react";
import "./NPSCalculator.css";

const inr = (n) =>
  "₹" + Math.round(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const TABS = [
  { id: "personal",      label: "Personal",       icon: "👤", step: 1 },
  { id: "contributions", label: "Contributions",  icon: "💰", step: 2 },
  { id: "portfolio",     label: "Portfolio",      icon: "📈", step: 3 },
];

export default function NPSCalculator() {
  const [activeTab, setActiveTab] = useState("personal");
  const [form, setForm] = useState({
    currentAge:       30,
    retirementAge:    60,
    monthlyEmployee:  3001,
    monthlyEmployer:  0,
    existingCorpus:   0,
    equityPct:        75,
    expectedReturn:   10,
    annuityPct:       40,
    annuityRate:      6,
    accountType:      "tier1",
  });
  const [result, setResult] = useState(null);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function calculate() {
    const curAge   = +form.currentAge;
    const retAge   = +form.retirementAge;
    const years    = retAge - curAge;
    if (years <= 0) return;

    const monthlyContrib = +form.monthlyEmployee + +form.monthlyEmployer;
    const annualContrib  = monthlyContrib * 12;
    const returnRate     = +form.expectedReturn / 100;
    const monthlyRate    = returnRate / 12;
    const totalMonths    = years * 12;
    const existingCorpus = +form.existingCorpus;

    const fvExisting = existingCorpus * Math.pow(1 + returnRate, years);
    const fvContribs = totalMonths > 0
      ? (+form.monthlyEmployee + +form.monthlyEmployer) *
        ((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate) *
        (1 + monthlyRate)
      : 0;
    const totalCorpus   = fvExisting + fvContribs;
    const totalInvested = annualContrib * years + existingCorpus;
    const totalGains    = Math.max(0, totalCorpus - totalInvested);

    const annPct         = +form.annuityPct / 100;
    const lumpsum        = totalCorpus * (1 - annPct);
    const annuityCorpus  = totalCorpus * annPct;
    const monthlyPension = (annuityCorpus * (+form.annuityRate / 100)) / 12;

    const empAnnual  = +form.monthlyEmployee * 12;
    const emplAnnual = +form.monthlyEmployer * 12;
    const ded80CCD1  = Math.min(empAnnual, 130010);
    const ded80CCD1B = Math.min(empAnnual, 30010);
    const ded80CCD2  = Math.min(emplAnnual, empAnnual * 0.14);
    const totalDed   = ded80CCD1 + ded80CCD1B + ded80CCD2;

    const yearlyData = [];
    let bal = existingCorpus;
    for (let y = 1; y <= Math.min(years, 35); y++) {
      const int = (bal + annualContrib) * returnRate;
      bal = bal + annualContrib + int;
      yearlyData.push({ y, age: curAge + y, interest: Math.round(int), balance: Math.round(bal) });
    }

    setResult({
      totalCorpus, totalInvested, totalGains,
      lumpsum, annuityCorpus, monthlyPension,
      years, ded80CCD1, ded80CCD1B, ded80CCD2, totalDed,
      fvExisting, fvContribs, empAnnual, emplAnnual, yearlyData,
    });
  }

  const currentTabIdx = TABS.findIndex((t) => t.id === activeTab);

  function goNext() {
    if (currentTabIdx < TABS.length - 1)
      setActiveTab(TABS[currentTabIdx + 1].id);
  }
  function goPrev() {
    if (currentTabIdx > 0)
      setActiveTab(TABS[currentTabIdx - 1].id);
  }

  return (
    <div className="nps-page">
      {/* ── HERO ── */}
      <div className="nps-hero">
        <span className="nps-badge">🏛️ Pension + Tax Saving</span>
<h1>
  NPS{" "}
  <span className="choose-title-accent">Calculator</span>
</h1>
        <p>National Pension System · Build retirement wealth · Save ₹2L+ in taxes annually</p>
        <div className="nps-hero-ded">
          <div className="nps-ded-item">
            <span className="nps-ded-val">₹1.5L</span>
            <span>80CCD(1) / 80C</span>
          </div>
          <div className="nps-ded-sep">+</div>
          <div className="nps-ded-item">
            <span className="nps-ded-val">₹50K</span>
            <span>80CCD(1B) Extra</span>
          </div>
          <div className="nps-ded-sep">+</div>
          <div className="nps-ded-item">
            <span className="nps-ded-val">14%</span>
            <span>Employer 80CCD(2)</span>
          </div>
        </div>
      </div>

      <div className="nps-wrapper">
        {/* ════ FORM COLUMN ════ */}
        <div className="nps-form-col">

          {/* ── Tab Bar ── */}
          <div className="nps-tab-bar">
            {TABS.map((tab, i) => (
              <button
                key={tab.id}
                className={`nps-tab-btn ${activeTab === tab.id ? "active" : ""} ${i < currentTabIdx ? "done" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="nps-tab-step">
                  {i < currentTabIdx ? "✓" : tab.step}
                </span>
                <span className="nps-tab-icon">{tab.icon}</span>
                <span className="nps-tab-label">{tab.label}</span>
              </button>
            ))}
            {/* progress line */}
            <div className="nps-tab-track">
              <div
                className="nps-tab-progress"
                style={{ width: `${(currentTabIdx / (TABS.length - 1)) * 100}%` }}
              />
            </div>
          </div>

          {/* ── Tab Panels ── */}
          <div className="nps-tab-panels">

            {/* ── Panel 1: Personal ── */}
            {activeTab === "personal" && (
              <div className="nps-panel" key="personal">
                <div className="nps-panel-hd">
                  <span className="nps-panel-icon">👤</span>
                  <div>
                    <h3>Personal Details</h3>
                    <p>Set your age and account type</p>
                  </div>
                </div>

                <div className="nps-card-bd">
                  <div className="nps-grid2">
                    <div className="nps-field">
                      <label>Current Age</label>
                      <input
                        type="number" className="nps-inp no-sym"
                        min="18" max="59"
                        value={form.currentAge}
                        onChange={(e) => upd("currentAge", e.target.value)}
                      />
                    </div>
                    <div className="nps-field">
                      <label>Retirement Age</label>
                      <input
                        type="number" className="nps-inp no-sym"
                        min="50" max="70"
                        value={form.retirementAge}
                        onChange={(e) => upd("retirementAge", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="nps-field">
                    <label>Account Type</label>
                    <div className="nps-toggle-group">
                      <button
                        className={`nps-tog ${form.accountType === "tier1" ? "active" : ""}`}
                        onClick={() => upd("accountType", "tier1")}
                      >
                        Tier I – Pension
                      </button>
                      <button
                        className={`nps-tog ${form.accountType === "tier2" ? "active" : ""}`}
                        onClick={() => upd("accountType", "tier2")}
                      >
                        Tier II – Savings
                      </button>
                    </div>
                    <div className="nps-field-note">
                      {form.accountType === "tier1"
                        ? "📋 Tax benefits available · Lock-in till retirement"
                        : "💼 No lock-in · No tax benefits · Full withdrawal anytime"}
                    </div>
                  </div>

                  <div className="nps-info-strip">
                    <div className="nps-info-item">
                      <span className="nps-info-lbl">Investment Period</span>
                      <span className="nps-info-val">
                        {Math.max(0, +form.retirementAge - +form.currentAge)} years
                      </span>
                    </div>
                    <div className="nps-info-item">
                      <span className="nps-info-lbl">Retirement at</span>
                      <span className="nps-info-val">Age {form.retirementAge}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Panel 2: Contributions ── */}
            {activeTab === "contributions" && (
              <div className="nps-panel" key="contributions">
                <div className="nps-panel-hd">
                  <span className="nps-panel-icon">💰</span>
                  <div>
                    <h3>Monthly Contributions</h3>
                    <p>Employee, employer & existing corpus</p>
                  </div>
                </div>

                <div className="nps-card-bd">
                  <div className="nps-field">
                    <label>
                      Employee Monthly Contribution&nbsp;
                      <span className="nps-range-val">{inr(+form.monthlyEmployee)}</span>
                    </label>
                    <div className="nps-inp-wrap">
                      <span className="nps-inp-sym">₹</span>
                      <input
                        type="number"
                        value={form.monthlyEmployee}
                        onChange={(e) => upd("monthlyEmployee", e.target.value)}
                      />
                    </div>
                    <input
                      type="range" min="500" max="30010" step="500"
                      value={form.monthlyEmployee}
                      onChange={(e) => upd("monthlyEmployee", +e.target.value)}
                    />
                    <div className="nps-slider-labels"><span>₹500</span><span>₹50K</span></div>
                  </div>

                  <div className="nps-field">
                    <label>Employer Monthly Contribution</label>
                    <div className="nps-inp-wrap">
                      <span className="nps-inp-sym">₹</span>
                      <input
                        type="number" placeholder="0"
                        value={form.monthlyEmployer}
                        onChange={(e) => upd("monthlyEmployer", e.target.value)}
                      />
                    </div>
                    <div className="nps-field-note">
                      💡 Employer contribution up to 14% of Basic is deductible under 80CCD(2) — even in New Regime!
                    </div>
                  </div>

                  <div className="nps-field">
                    <label>Existing NPS Corpus</label>
                    <div className="nps-inp-wrap">
                      <span className="nps-inp-sym">₹</span>
                      <input
                        type="number" placeholder="0"
                        value={form.existingCorpus}
                        onChange={(e) => upd("existingCorpus", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="nps-info-strip">
                    <div className="nps-info-item">
                      <span className="nps-info-lbl">Total Monthly</span>
                      <span className="nps-info-val">
                        {inr(+form.monthlyEmployee + +form.monthlyEmployer)}
                      </span>
                    </div>
                    <div className="nps-info-item">
                      <span className="nps-info-lbl">Annual Contribution</span>
                      <span className="nps-info-val">
                        {inr((+form.monthlyEmployee + +form.monthlyEmployer) * 12)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Panel 3: Portfolio & Annuity ── */}
            {activeTab === "portfolio" && (
              <div className="nps-panel" key="portfolio">
                <div className="nps-panel-hd">
                  <span className="nps-panel-icon">📈</span>
                  <div>
                    <h3>Portfolio & Annuity</h3>
                    <p>Allocation, returns and pension setup</p>
                  </div>
                </div>

                <div className="nps-card-bd">
                  {/* Equity Allocation */}
                  <div className="nps-field">
                    <label>
                      Equity Allocation&nbsp;
                      <strong className="nps-range-val">{form.equityPct}%</strong>
                      <span className="nps-limit-tag">Max 75%</span>
                    </label>
                    <input
                      type="range" min="0" max="75" step="5"
                      value={form.equityPct}
                      onChange={(e) => upd("equityPct", +e.target.value)}
                    />
                    <div className="nps-asset-bar">
                      <div className="nps-asset-seg" style={{ width: `${form.equityPct}%`, background: "#263012" }} />
                      <div className="nps-asset-seg" style={{ width: `${(100 - form.equityPct) / 2}%`, background: "#3b82f6" }} />
                      <div className="nps-asset-seg" style={{ width: `${(100 - form.equityPct) / 2}%`, background: "#263012" }} />
                    </div>
                    <div className="nps-asset-labels">
                      <span style={{ color: "#263012" }}>Equity {form.equityPct}%</span>
                      <span style={{ color: "#3b82f6" }}>Corp Bond {((100 - form.equityPct) / 2).toFixed(0)}%</span>
                      <span style={{ color: "#263012" }}>Govt Sec {((100 - form.equityPct) / 2).toFixed(0)}%</span>
                    </div>
                  </div>

                  {/* Expected Return */}
                  <div className="nps-field">
                    <label>
                      Expected Annual Return&nbsp;
                      <strong className="nps-range-val">{form.expectedReturn}%</strong>
                    </label>
                    <input
                      type="range" min="6" max="14" step="0.5"
                      value={form.expectedReturn}
                      onChange={(e) => upd("expectedReturn", +e.target.value)}
                    />
                    <div className="nps-slider-labels"><span>6%</span><span>10%</span><span>14%</span></div>
                  </div>

                  {/* Annuity % */}
                  <div className="nps-field">
                    <label>
                      Annuity Purchase %&nbsp;
                      <span className="nps-limit-tag">Min 40% mandatory</span>
                    </label>
                    <div className="nps-toggle-group">
                      {[40, 50, 60, 80, 100].map((v) => (
                        <button
                          key={v}
                          className={`nps-tog ${form.annuityPct === v ? "active" : ""}`}
                          onClick={() => upd("annuityPct", v)}
                        >
                          {v}%
                        </button>
                      ))}
                    </div>
                    <div className="nps-field-note">
                      {form.annuityPct === 100
                        ? "🔒 100% annuity — full monthly pension, no lump sum"
                        : `💰 ${100 - form.annuityPct}% lump sum (tax-free) + ${form.annuityPct}% monthly pension`}
                    </div>
                  </div>

                  {/* Annuity Rate */}
                  <div className="nps-field">
                    <label>
                      Expected Annuity Rate&nbsp;
                      <strong className="nps-range-val">{form.annuityRate}% p.a.</strong>
                    </label>
                    <input
                      type="range" min="4" max="9" step="0.5"
                      value={form.annuityRate}
                      onChange={(e) => upd("annuityRate", +e.target.value)}
                    />
                    <div className="nps-slider-labels"><span>4%</span><span>6.5%</span><span>9%</span></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Tab Navigation Footer ── */}
          <div className="nps-tab-footer">
            <button
              className="nps-nav-btn nps-nav-prev"
              onClick={goPrev}
              disabled={currentTabIdx === 0}
            >
              ← Back
            </button>

            {currentTabIdx < TABS.length - 1 ? (
              <button className="nps-nav-btn nps-nav-next" onClick={goNext}>
                Next →
              </button>
            ) : (
              <button className="nps-calc-btn" onClick={calculate}>
                Calculate NPS Corpus →
              </button>
            )}
          </div>
        </div>

        {/* ════ RESULTS COLUMN ════ */}
        <div className="nps-result-col">
          {!result ? (
            <div className="nps-empty">
              <div className="nps-empty-icon">🏛️</div>
              <h3>Calculate your NPS retirement corpus</h3>
              <p>Fill in the 3 tabs and hit Calculate to see your full projection</p>
              <div className="nps-empty-steps">
                {TABS.map((tab, i) => (
                  <div
                    key={tab.id}
                    className={`nps-empty-step ${activeTab === tab.id ? "active" : ""} ${i < currentTabIdx ? "done" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <span className="nps-empty-step-num">
                      {i < currentTabIdx ? "✓" : i + 1}
                    </span>
                    <span>{tab.icon} {tab.label}</span>
                  </div>
                ))}
              </div>
              <div className="nps-empty-grid">
                <div className="nps-empty-item">
                  <strong>80CCD(1)</strong>
                  <span>Up to ₹1.5L in 80C</span>
                </div>
                <div className="nps-empty-item">
                  <strong>80CCD(1B)</strong>
                  <span>Extra ₹50,000</span>
                </div>
                <div className="nps-empty-item">
                  <strong>80CCD(2)</strong>
                  <span>Employer 14% Basic</span>
                </div>
                <div className="nps-empty-item">
                  <strong>New Regime</strong>
                  <span>80CCD(2) allowed</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="nps-results-wrap">
              {/* ── Hero Corpus Card ── */}
              <div className="nps-corpus-card">
                <div className="nps-corpus-main">
                  <div className="nps-corpus-lbl">Total NPS Corpus at Retirement</div>
                  <div className="nps-corpus-val">{inr(result.totalCorpus)}</div>
                  <div className="nps-corpus-sub">
                    In {result.years} years · at {form.expectedReturn}% p.a. returns
                  </div>
                </div>
                <div className="nps-corpus-split">
                  <div className="nps-split-item green">
                    <div className="nps-split-lbl">Lump Sum ({100 - form.annuityPct}%) Tax-Free</div>
                    <div className="nps-split-val">{inr(result.lumpsum)}</div>
                  </div>
                  <div className="nps-split-sep">+</div>
                  <div className="nps-split-item orange">
                    <div className="nps-split-lbl">Annuity ({form.annuityPct}%)</div>
                    <div className="nps-split-val">{inr(result.annuityCorpus)}</div>
                  </div>
                  <div className="nps-split-sep">→</div>
                  <div className="nps-split-item blue">
                    <div className="nps-split-lbl">Monthly Pension</div>
                    <div className="nps-split-val">{inr(result.monthlyPension)}</div>
                  </div>
                </div>
              </div>

              {/* ── Tax Deduction Breakdown ── */}
              <div className="nps-card">
                <div className="nps-card-hd">
                  <div className="nps-card-icon">💰</div>
                  <h3>Annual Tax Deduction Breakdown</h3>
                </div>
                <div className="nps-card-bd">
                  <div className="nps-tax-grid">
                    <div className="nps-tax-item">
                      <div className="nps-tax-sec">80C / 80CCD(1)</div>
                      <div className="nps-tax-amt">{inr(result.ded80CCD1)}</div>
                      <div className="nps-tax-note">Employee contribution within 80C</div>
                    </div>
                    <div className="nps-tax-item nps-tax-item--highlight">
                      <div className="nps-tax-sec">80CCD(1B) — Exclusive!</div>
                      <div className="nps-tax-amt orange">{inr(result.ded80CCD1B)}</div>
                      <div className="nps-tax-note">Extra ₹50,000 over &amp; above 80C limit</div>
                    </div>
                    <div className="nps-tax-item">
                      <div className="nps-tax-sec">80CCD(2) — Employer</div>
                      <div className="nps-tax-amt">{inr(result.ded80CCD2)}</div>
                      <div className="nps-tax-note">Available in both Old &amp; New Regime!</div>
                    </div>
                  </div>
                  <div className="nps-tax-total">
                    <span>Total Annual Tax Deduction</span>
                    <strong>{inr(result.totalDed)}</strong>
                  </div>
                </div>
              </div>

              {/* ── Corpus Composition ── */}
              <div className="nps-card">
                <div className="nps-card-hd">
                  <div className="nps-card-icon">📈</div>
                  <h3>Corpus Composition</h3>
                </div>
                <div className="nps-card-bd">
                  {[
                    { label: "Total Amount Invested", value: result.totalInvested, color: "#3b82f6" },
                    { label: "Total Investment Gains", value: result.totalGains,   color: "#263012" },
                  ].map((item) => {
                    const pct = (item.value / result.totalCorpus * 100).toFixed(1);
                    return (
                      <div key={item.label} className="nps-bar-row">
                        <div className="nps-bar-meta">
                          <span className="nps-bar-dot" style={{ background: item.color }} />
                          <span className="nps-bar-lbl">{item.label}</span>
                          <span className="nps-bar-pct">{pct}%</span>
                          <strong className="nps-bar-val">{inr(item.value)}</strong>
                        </div>
                        <div className="nps-bar-track">
                          <div className="nps-bar-fill" style={{ width: `${pct}%`, background: item.color }} />
                        </div>
                      </div>
                    );
                  })}
                  <div className="nps-comp-stats">
                    <div className="nps-comp-stat">
                      <div className="nps-comp-lbl">Total Invested</div>
                      <div className="nps-comp-val blue">{inr(result.totalInvested)}</div>
                    </div>
                    <div className="nps-comp-stat">
                      <div className="nps-comp-lbl">Gains Earned</div>
                      <div className="nps-comp-val green">{inr(result.totalGains)}</div>
                    </div>
                    <div className="nps-comp-stat">
                      <div className="nps-comp-lbl">Wealth Multiple</div>
                      <div className="nps-comp-val orange">{(result.totalCorpus / (result.totalInvested || 1)).toFixed(2)}x</div>
                    </div>
                    <div className="nps-comp-stat">
                      <div className="nps-comp-lbl">Annual Return</div>
                      <div className="nps-comp-val">{form.expectedReturn}%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Year-wise Table ── */}
              <div className="nps-card">
                <div className="nps-card-hd">
                  <div className="nps-card-icon">📅</div>
                  <h3>Year-wise NPS Growth</h3>
                </div>
                <div className="nps-table-wrap">
                  <table className="nps-table">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Age</th>
                        <th>Annual Gains</th>
                        <th>Total Corpus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.yearlyData.map((row, i) => (
                        <tr key={i} className={i === result.yearlyData.length - 1 ? "nps-tr--final" : ""}>
                          <td className="nps-td-c">{row.y}</td>
                          <td className="nps-td-c">{row.age}</td>
                          <td className="nps-td-r nps-green">{inr(row.interest)}</td>
                          <td className="nps-td-r"><strong className="nps-orange">{inr(row.balance)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recalculate */}
              <button
                className="nps-calc-btn"
                onClick={() => { setActiveTab("personal"); setResult(null); }}
                style={{ marginTop: 4 }}
              >
                ← Recalculate
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}